import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box, Typography, Paper, IconButton, Tabs, Tab, Chip,
    Card, CardMedia, CardContent, Grid, Fab, Skeleton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Tooltip, LinearProgress, Alert,
    ImageList, ImageListItem, ImageListItemBar, Menu, MenuItem,
    Drawer, List, ListItem, ListItemIcon, ListItemText, Divider,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CollectionsIcon from '@mui/icons-material/Collections';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MicIcon from '@mui/icons-material/Mic';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import PeopleIcon from '@mui/icons-material/People';
import { API_BASE_URL } from './config';

// Import API service
import memoriesApi from './utils/memoriesApi';

// Import mock data as fallback (will be removed in Phase 2)
import {
    mockTrips,
    getDaysForTrip,
    getMemoriesForDay,
    getDayNote,
    getActivitiesForDay,
    getMemoriesForTrip,
    getHighlightsForTrip,
    getTripById,
} from './data/mockMemories';

// ===============================
// Sub-components
// ===============================

// Skeleton loader for day cards
const DayCardSkeleton = () => (
    <div className="tw-rounded-2xl tw-bg-surface-glass tw-backdrop-blur-md tw-border tw-border-surface-border tw-mb-3 tw-animate-pulse">
        <div className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3">
            <div className="tw-flex-shrink-0 tw-w-12 tw-h-16 tw-rounded-xl tw-bg-surface-3" />
            <div className="tw-flex-1 tw-space-y-2">
                <div className="tw-h-4 tw-rounded tw-bg-surface-3" style={{ width: '60%' }} />
                <div className="tw-h-3 tw-rounded tw-bg-surface-3" style={{ width: '40%' }} />
            </div>
        </div>
    </div>
);

// Small note/comment icon shown on media that carries a note
const NoteBadge = () => (
    <span className="tw-absolute tw-top-1.5 tw-left-1.5 tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-full tw-bg-black/55 tw-text-white tw-drop-shadow-md" aria-label="Has a note">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
    </span>
);

// Media Thumbnail component
const MediaThumbnail = ({ memory, onClick, size = 'medium', showCaption = false }) => {
    const sizeClass = size === 'small' ? 'tw-w-16 tw-h-16' : size === 'large' ? 'tw-w-48 tw-h-48' : 'tw-w-[120px] tw-h-[120px]';
    const hasNote = !!(memory.caption && memory.caption.trim());
    return (
        <div
            onClick={() => onClick?.(memory)}
            className={`tw-relative tw-flex-shrink-0 tw-cursor-pointer tw-rounded-xl tw-overflow-hidden tw-group ${sizeClass}`}
            title={hasNote ? memory.caption : undefined}
        >
            {memory.mediaType === 'video' ? (
                <div className="tw-w-full tw-h-full tw-bg-surface-3 tw-flex tw-items-center tw-justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor" className="tw-text-white/60"><path d="M8 5v14l11-7z"/></svg>
                </div>
            ) : (
                <img
                    src={memory.thumbnail || memory.mediaPath}
                    alt={memory.caption || 'Memory'}
                    className="tw-w-full tw-h-full tw-object-cover"
                />
            )}
            {memory.isHighlight && (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="tw-absolute tw-top-1.5 tw-right-1.5 tw-text-amber-400 tw-drop-shadow-md" aria-hidden="true">
                    <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                </svg>
            )}
            {hasNote && <NoteBadge />}
            {/* Note popup on hover (also covers the old showCaption behaviour) */}
            {hasNote && (
                <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-bg-black/70 tw-py-1 tw-px-1.5 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-pointer-events-none">
                    <p className={`tw-text-xs tw-text-white ${size === 'small' ? 'tw-truncate' : 'tw-line-clamp-3'}`}>{memory.caption}</p>
                </div>
            )}
        </div>
    );
};

// Day Card component for Timeline view
const DayCard = ({ day, memories, note, isExpanded, onToggle, onViewDay, onMediaClick }) => {
    const { t } = useTranslation();
    const highlights = memories.filter(m => m.isHighlight);
    const displayMemories = highlights.length > 0 ? highlights.slice(0, 4) : memories.slice(0, 4);
    const dayDate = new Date(day.date);

    return (
        <div
            onClick={() => onViewDay(day)}
            className="tw-rounded-2xl tw-bg-surface-glass tw-backdrop-blur-md tw-border tw-border-surface-border tw-mb-3 tw-cursor-pointer tw-transition-all tw-duration-200 hover:tw-border-accent/40 hover:tw-bg-surface-3/60"
        >
            <div className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3">
                {/* Date badge */}
                <div className="tw-flex-shrink-0 tw-w-12 tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-bg-accent/15 tw-border tw-border-accent/25 tw-py-2">
                    <span className="tw-text-lg tw-font-bold tw-text-accent tw-leading-none">
                        {dayDate.getDate()}
                    </span>
                    <span className="tw-text-xs tw-text-accent/70 tw-font-medium tw-mt-0.5">
                        {dayDate.toLocaleDateString('en-US', { weekday: 'short' })}
                    </span>
                </div>

                {/* Day info */}
                <div className="tw-flex-1 tw-min-w-0">
                    <p className="tw-text-sm tw-font-bold tw-text-muted-fore tw-leading-snug tw-truncate">
                        {day.title || dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mt-1 tw-flex-wrap">
                        <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-muted">
                            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <circle cx="12" cy="12" r="3.2"/>
                                <path d="M9 2 7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5"/>
                            </svg>
                            {memories.length} {memories.length === 1 ? 'memory' : 'memories'}
                        </span>
                        {highlights.length > 0 && (
                            <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-amber-400">
                                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                                </svg>
                                {highlights.length} highlight{highlights.length !== 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                    {note && (
                        <p className="tw-text-xs tw-text-muted tw-mt-1 tw-line-clamp-2 tw-leading-relaxed">
                            {note}
                        </p>
                    )}
                </div>

                {/* Chevron */}
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tw-flex-shrink-0 tw-text-muted" aria-hidden="true">
                    <path d="m9 18 6-6-6-6"/>
                </svg>
            </div>

            {/* Memory thumbnails preview */}
            {displayMemories.length > 0 && (
                <div
                    className="tw-flex tw-gap-1.5 tw-px-4 tw-pb-3 tw-overflow-x-auto"
                    style={{ scrollbarWidth: 'thin' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {displayMemories.map((memory) => (
                        <MediaThumbnail
                            key={memory.id}
                            memory={memory}
                            size="small"
                            onClick={(m) => onMediaClick(m, memories)}
                        />
                    ))}
                    {memories.length > 4 && (
                        <div className="tw-flex-shrink-0 tw-w-14 tw-h-14 tw-rounded-xl tw-bg-surface-3 tw-border tw-border-surface-border tw-flex tw-items-center tw-justify-center">
                            <span className="tw-text-xs tw-font-bold tw-text-muted">+{memories.length - 4}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Fullscreen Media Viewer
const MediaViewer = ({ open, memories, currentIndex, onClose, onNavigate, onToggleHighlight, onEditCaption, onDelete }) => {
    const currentMemory = memories[currentIndex];
    const [editingCaption, setEditingCaption] = useState(false);
    const [captionText, setCaptionText] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        if (currentMemory) setCaptionText(currentMemory.caption || '');
        setEditingCaption(false);
        setConfirmDelete(false);
    }, [currentMemory]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'ArrowLeft' && currentIndex > 0) onNavigate(currentIndex - 1);
        else if (e.key === 'ArrowRight' && currentIndex < memories.length - 1) onNavigate(currentIndex + 1);
        else if (e.key === 'Escape') onClose();
    }, [currentIndex, memories.length, onNavigate, onClose]);

    useEffect(() => {
        if (open) {
            window.addEventListener('keydown', handleKeyDown);
            return () => window.removeEventListener('keydown', handleKeyDown);
        }
    }, [open, handleKeyDown]);

    if (!open || !currentMemory) return null;

    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-bg-black tw-flex tw-flex-col">
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-bg-black/80 tw-backdrop-blur-sm tw-border-b tw-border-white/10 tw-flex-shrink-0">
                <button onClick={onClose} className="tw-p-2 tw-rounded-xl tw-text-white/70 hover:tw-text-white hover:tw-bg-white/10 tw-transition-all" aria-label="Close">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                </button>
                <span className="tw-text-sm tw-text-white/60 tw-tabular-nums">{currentIndex + 1} / {memories.length}</span>
                <div className="tw-flex tw-items-center tw-gap-1">
                    <button
                        onClick={() => onToggleHighlight(currentMemory.id)}
                        className={`tw-p-2 tw-rounded-xl tw-transition-all ${currentMemory.isHighlight ? 'tw-text-amber-400 tw-bg-amber-500/20' : 'tw-text-white/50 hover:tw-text-amber-400 hover:tw-bg-amber-500/10'}`}
                        aria-label="Toggle highlight"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill={currentMemory.isHighlight ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"><path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                    </button>
                    <button onClick={() => setEditingCaption(true)} className="tw-p-2 tw-rounded-xl tw-text-white/50 hover:tw-text-white hover:tw-bg-white/10 tw-transition-all" aria-label="Edit caption">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                    {onDelete && (
                        <button onClick={() => setConfirmDelete(true)} className="tw-p-2 tw-rounded-xl tw-text-white/50 hover:tw-text-red-400 hover:tw-bg-red-500/10 tw-transition-all" aria-label="Delete media">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                        </button>
                    )}
                </div>
            </div>

            {/* Delete confirmation overlay */}
            {confirmDelete && (
                <div className="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-black/70 tw-p-4">
                    <div className="tw-w-full tw-max-w-xs tw-rounded-2xl tw-bg-surface-2 tw-border tw-border-surface-border tw-p-5 tw-text-center">
                        <p className="tw-text-base tw-font-bold tw-text-muted-fore tw-mb-1">Delete this {currentMemory.mediaType === 'video' ? 'video' : 'photo'}?</p>
                        <p className="tw-text-sm tw-text-muted tw-mb-4">This memory will be removed from the trip.</p>
                        <div className="tw-flex tw-justify-center tw-gap-2">
                            <button onClick={() => setConfirmDelete(false)} className="tw-px-4 tw-py-2 tw-rounded-xl tw-text-sm tw-text-muted hover:tw-text-muted-fore hover:tw-bg-surface-3 tw-transition-all">Cancel</button>
                            <button onClick={() => { setConfirmDelete(false); onDelete(currentMemory.id); }} className="tw-px-4 tw-py-2 tw-rounded-xl tw-text-sm tw-font-semibold tw-bg-red-500 tw-text-white hover:tw-bg-red-600 tw-transition-all">Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Media */}
            <div className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-relative tw-overflow-hidden">
                {currentMemory.mediaType === 'video' ? (
                    <video src={currentMemory.mediaPath} controls className="tw-max-w-full tw-max-h-full" />
                ) : (
                    <img src={currentMemory.mediaPath} alt={currentMemory.caption || 'Memory'} className="tw-max-w-full tw-max-h-full tw-object-contain" />
                )}
                {currentIndex > 0 && (
                    <button onClick={() => onNavigate(currentIndex - 1)} className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-p-2.5 tw-rounded-xl tw-bg-black/50 tw-text-white hover:tw-bg-black/70 tw-transition-all" aria-label="Previous">
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"/></svg>
                    </button>
                )}
                {currentIndex < memories.length - 1 && (
                    <button onClick={() => onNavigate(currentIndex + 1)} className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-p-2.5 tw-rounded-xl tw-bg-black/50 tw-text-white hover:tw-bg-black/70 tw-transition-all" aria-label="Next">
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6"/></svg>
                    </button>
                )}
            </div>

            {/* Caption bar */}
            <div className="tw-bg-black/80 tw-backdrop-blur-sm tw-border-t tw-border-white/10 tw-px-4 tw-py-3 tw-flex-shrink-0">
                {editingCaption ? (
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <input
                            type="text"
                            value={captionText}
                            onChange={(e) => setCaptionText(e.target.value)}
                            placeholder="Add a note..."
                            className="tw-flex-1 tw-bg-white/10 tw-border tw-border-white/20 tw-rounded-xl tw-px-3 tw-py-2 tw-text-sm tw-text-white placeholder:tw-text-white/40 tw-outline-none focus:tw-border-accent/60"
                            autoFocus
                        />
                        <button onClick={() => { onEditCaption(currentMemory.id, captionText.trim()); setEditingCaption(false); }} className="tw-px-3 tw-py-2 tw-rounded-xl tw-bg-accent tw-text-white tw-text-sm tw-font-semibold hover:tw-opacity-90 tw-transition-all">Save</button>
                        {(currentMemory.caption || captionText) && (
                            <button onClick={() => { setCaptionText(''); onEditCaption(currentMemory.id, ''); setEditingCaption(false); }} className="tw-px-3 tw-py-2 tw-rounded-xl tw-bg-red-500/20 tw-text-red-300 tw-text-sm hover:tw-bg-red-500/30 tw-transition-all" title="Remove note">Remove</button>
                        )}
                        <button onClick={() => setEditingCaption(false)} className="tw-px-3 tw-py-2 tw-rounded-xl tw-bg-white/10 tw-text-white/70 tw-text-sm hover:tw-bg-white/20 tw-transition-all">Cancel</button>
                    </div>
                ) : (
                    <div>
                        <p className="tw-text-sm tw-text-white/80 tw-leading-snug">{currentMemory.caption || <span className="tw-text-white/40">No note</span>}</p>
                        {currentMemory.takenAt && (
                            <p className="tw-text-xs tw-text-white/40 tw-mt-0.5">{new Date(currentMemory.takenAt).toLocaleString()}</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// Upload Modal component — lets the user attach a note/caption to each photo or video
let uploadItemSeq = 0;
const UploadModal = ({ open, onClose, dayId, onUpload }) => {
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState(null);
    // Each item: { id, file, previewUrl, mediaType, caption }
    const [items, setItems] = useState([]);

    const buildItems = (files) =>
        files
            .filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'))
            .map(file => ({
                id: `u${uploadItemSeq++}`,
                file,
                previewUrl: URL.createObjectURL(file),
                mediaType: file.type.startsWith('video/') ? 'video' : 'image',
                caption: '',
            }));

    const addFiles = (files) => {
        const newItems = buildItems(files);
        if (newItems.length > 0) setItems(prev => [...prev, ...newItems]);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        addFiles(Array.from(e.dataTransfer.files));
    };

    const handleFileSelect = (e) => {
        addFiles(Array.from(e.target.files));
        e.target.value = ''; // allow re-selecting the same file
    };

    const setCaption = (id, caption) => {
        setItems(prev => prev.map(it => (it.id === id ? { ...it, caption } : it)));
    };

    const removeItem = (id) => {
        setItems(prev => {
            const target = prev.find(it => it.id === id);
            if (target) URL.revokeObjectURL(target.previewUrl);
            return prev.filter(it => it.id !== id);
        });
    };

    // Clean up object URLs when the modal closes / unmounts
    const cleanupPreviews = useCallback(() => {
        setItems(prev => {
            prev.forEach(it => URL.revokeObjectURL(it.previewUrl));
            return [];
        });
    }, []);

    useEffect(() => {
        return () => {
            setItems(prev => {
                prev.forEach(it => URL.revokeObjectURL(it.previewUrl));
                return prev;
            });
        };
    }, []);

    const handleClose = () => {
        if (uploading) return;
        cleanupPreviews();
        setUploadError(null);
        onClose();
    };

    const handleUpload = async () => {
        if (items.length === 0) return;
        setUploading(true);
        setUploadError(null);
        setUploadProgress(0);
        try {
            // Pass each file together with its note/caption to the parent handler.
            await onUpload(
                items.map(it => ({ file: it.file, caption: it.caption.trim() })),
                (percent) => setUploadProgress(percent)
            );
            cleanupPreviews();
            setUploading(false);
            setUploadProgress(0);
            onClose();
        } catch (err) {
            setUploading(false);
            setUploadError(err?.message || 'Upload failed. Please try again.');
        }
    };

    if (!open) return null;

    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/70 tw-p-4" onClick={handleClose}>
            <div className="tw-w-full tw-max-w-md tw-max-h-[90vh] tw-flex tw-flex-col tw-rounded-2xl tw-bg-surface-2 tw-border tw-border-surface-border tw-shadow-2xl" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-pt-5 tw-pb-3 tw-border-b tw-border-surface-border/40 tw-flex-shrink-0">
                    <h6 className="tw-text-base tw-font-bold tw-text-muted-fore">Upload Memories</h6>
                    <button onClick={handleClose} className="tw-p-1.5 tw-rounded-xl tw-text-muted hover:tw-text-muted-fore hover:tw-bg-surface-3 tw-transition-all" aria-label="Close">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </button>
                </div>
                {/* Content */}
                <div className="tw-p-5 tw-space-y-4 tw-overflow-y-auto">
                    {/* Drop zone */}
                    <label
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={handleDrop}
                        className={`tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-border-2 tw-border-dashed tw-rounded-2xl tw-p-8 tw-cursor-pointer tw-transition-all ${dragOver ? 'tw-border-accent tw-bg-accent/10' : 'tw-border-surface-border tw-bg-surface-3/30 hover:tw-border-accent/40 hover:tw-bg-surface-3/60'}`}
                    >
                        <input type="file" hidden multiple accept="image/*,video/*" onChange={handleFileSelect} />
                        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="tw-text-accent/60"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        <p className="tw-text-sm tw-text-muted-fore">Drag photos &amp; videos here</p>
                        <p className="tw-text-xs tw-text-muted">or click to browse</p>
                    </label>
                    {/* Selected media with per-item note */}
                    {items.length > 0 && (
                        <div>
                            <p className="tw-text-xs tw-font-semibold tw-text-muted tw-mb-2">Selected ({items.length} {items.length === 1 ? 'item' : 'items'}) — add a note to each</p>
                            <div className="tw-space-y-2.5">
                                {items.map((it) => (
                                    <div key={it.id} className="tw-flex tw-gap-3 tw-p-2 tw-rounded-xl tw-bg-surface-3/40 tw-border tw-border-surface-border/40">
                                        {/* Preview */}
                                        <div className="tw-relative tw-w-16 tw-h-16 tw-rounded-lg tw-overflow-hidden tw-flex-shrink-0 tw-bg-surface-3">
                                            {it.mediaType === 'video' ? (
                                                <>
                                                    <video src={it.previewUrl} className="tw-w-full tw-h-full tw-object-cover" muted playsInline preload="metadata" />
                                                    <span className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/25">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="tw-text-white/90"><path d="M8 5v14l11-7z"/></svg>
                                                    </span>
                                                </>
                                            ) : (
                                                <img src={it.previewUrl} alt="" className="tw-w-full tw-h-full tw-object-cover" />
                                            )}
                                        </div>
                                        {/* Note input */}
                                        <div className="tw-flex-1 tw-min-w-0">
                                            <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                                                <span className="tw-text-xs tw-text-muted tw-truncate tw-mb-1">{it.file.name}</span>
                                                <button onClick={() => removeItem(it.id)} disabled={uploading} className="tw-p-0.5 tw-rounded-md tw-text-muted hover:tw-text-red-400 hover:tw-bg-surface-3 tw-transition-all tw-flex-shrink-0 disabled:tw-opacity-40" aria-label="Remove">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                                                </button>
                                            </div>
                                            <textarea
                                                value={it.caption}
                                                onChange={(e) => setCaption(it.id, e.target.value)}
                                                disabled={uploading}
                                                placeholder={it.mediaType === 'video' ? 'Add a note for this video…' : 'Add a note for this photo…'}
                                                rows={2}
                                                className="tw-w-full tw-bg-surface-2 tw-border tw-border-surface-border tw-rounded-lg tw-px-2.5 tw-py-1.5 tw-text-sm tw-text-muted-fore placeholder:tw-text-muted/50 tw-resize-none tw-outline-none focus:tw-border-accent/60 disabled:tw-opacity-60"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {/* Error */}
                    {uploadError && (
                        <p className="tw-text-xs tw-text-red-400 tw-bg-red-500/10 tw-border tw-border-red-500/20 tw-rounded-lg tw-px-3 tw-py-2">{uploadError}</p>
                    )}
                    {/* Progress */}
                    {uploading && (
                        <div>
                            <div className="tw-w-full tw-h-1.5 tw-bg-surface-3 tw-rounded-full tw-overflow-hidden">
                                <div className="tw-h-full tw-bg-accent tw-rounded-full tw-transition-all tw-duration-300" style={{ width: `${uploadProgress}%` }} />
                            </div>
                            <p className="tw-text-xs tw-text-muted tw-mt-1">Uploading… {uploadProgress}%</p>
                        </div>
                    )}
                </div>
                {/* Footer */}
                <div className="tw-flex tw-justify-end tw-gap-2 tw-px-5 tw-pb-5 tw-pt-3 tw-flex-shrink-0 tw-border-t tw-border-surface-border/40">
                    <button onClick={handleClose} disabled={uploading} className="tw-px-4 tw-py-2 tw-rounded-xl tw-text-sm tw-text-muted hover:tw-text-muted-fore hover:tw-bg-surface-3 tw-transition-all disabled:tw-opacity-40">Cancel</button>
                    <button onClick={handleUpload} disabled={items.length === 0 || uploading} className="tw-px-4 tw-py-2 tw-rounded-xl tw-text-sm tw-font-semibold tw-bg-accent tw-text-white hover:tw-opacity-90 tw-transition-all disabled:tw-opacity-40 disabled:tw-cursor-not-allowed">
                        Upload {items.length > 0 ? `(${items.length})` : ''}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Note Editor component
const NoteEditor = ({ dayId, initialNote, onSave }) => {
    const [note, setNote] = useState(initialNote || '');
    const [isEditing, setIsEditing] = useState(false);

    return (
        <div className="tw-rounded-2xl tw-bg-surface-glass tw-backdrop-blur-md tw-border tw-border-surface-border tw-p-4 tw-mb-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                <span className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-bold tw-text-muted-fore">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="tw-text-accent" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                    Day Notes
                </span>
                {!isEditing && (
                    <button onClick={() => setIsEditing(true)} className="tw-p-1.5 tw-rounded-xl tw-text-muted hover:tw-text-muted-fore hover:tw-bg-surface-3 tw-transition-all" aria-label="Edit note">
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                )}
            </div>
            {isEditing ? (
                <div>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Write about your day..."
                        className="tw-w-full tw-bg-surface-3/60 tw-border tw-border-surface-border tw-rounded-xl tw-px-3 tw-py-2 tw-text-sm tw-text-muted-fore placeholder:tw-text-muted/50 tw-resize-none tw-min-h-[80px] tw-outline-none focus:tw-border-accent/60"
                        rows={3}
                    />
                    <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-2">
                        <button onClick={() => setIsEditing(false)} className="tw-px-3 tw-py-1.5 tw-rounded-xl tw-text-sm tw-text-muted hover:tw-text-muted-fore hover:tw-bg-surface-3 tw-transition-all">Cancel</button>
                        <button onClick={() => { onSave(note); setIsEditing(false); }} className="tw-px-3 tw-py-1.5 tw-rounded-xl tw-text-sm tw-font-semibold tw-bg-accent/20 tw-text-accent hover:tw-bg-accent/30 tw-transition-all">Save</button>
                    </div>
                </div>
            ) : (
                <p className={`tw-text-sm tw-leading-relaxed ${note ? 'tw-text-muted-fore' : 'tw-text-muted'}`}>
                    {note || 'No notes yet. Click edit to add your thoughts…'}
                </p>
            )}
        </div>
    );
};

// ===============================
// Main MemoriesPage Component
// ===============================

const MemoriesPage = () => {
    const { t } = useTranslation();
    const { tripId } = useParams();
    const navigate = useNavigate();
    
    // State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [trip, setTrip] = useState(null);
    const [days, setDays] = useState([]);
    const [selectedDay, setSelectedDay] = useState(null);
    const [viewMode, setViewMode] = useState('timeline'); // 'timeline' | 'gallery' | 'highlights'
    const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
    const [viewerMemories, setViewerMemories] = useState([]);
    const [viewerIndex, setViewerIndex] = useState(0);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [uploadDayId, setUploadDayId] = useState(null);
    const [useMockData, setUseMockData] = useState(false);

    // Load trip data
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            setError(null);
            
            try {
                // Try to fetch from API first
                const data = await memoriesApi.getTripMemoriesData(tripId);
                
                // Transform API data to match component structure
                const enrichedDays = data.days.map(day => ({
                    id: day.id,
                    tripId: tripId,
                    date: day.date,
                    title: day.title,
                    order: day.order,
                    memories: (day.memories || []).map(m => ({
                        ...m,
                        isHighlight: m.is_highlight,
                        mediaPath: m.media_path?.startsWith('http') ? m.media_path : `${API_BASE_URL.replace('/backend/api', '')}/${m.media_path}`,
                        thumbnail: m.thumbnail_path ? (m.thumbnail_path.startsWith('http') ? m.thumbnail_path : `${API_BASE_URL.replace('/backend/api', '')}/${m.thumbnail_path}`) : null,
                        takenAt: m.taken_at,
                        activityId: m.activity_id,
                        mediaType: m.media_type,
                    })),
                    note: day.note || '',
                    activities: day.activities || [],
                }));
                
                setTrip({
                    id: data.trip.trip_id,
                    name: data.trip.name,
                    startDate: data.trip.start_date,
                    endDate: data.trip.end_date,
                });
                setDays(enrichedDays);
                setUseMockData(false);
            } catch (err) {
                console.warn('API failed, falling back to mock data:', err.message);
                
                // Fall back to mock data
                const tripData = getTripById(tripId);
                const tripDays = getDaysForTrip(Number(tripId));
                
                const enrichedDays = tripDays.map(day => ({
                    ...day,
                    memories: getMemoriesForDay(day.id),
                    note: getDayNote(day.id),
                    activities: getActivitiesForDay(day.id),
                }));
                
                setTrip(tripData);
                setDays(enrichedDays);
                setUseMockData(true);
            }
            
            setLoading(false);
        };
        
        loadData();
    }, [tripId]);

    // Get all memories for the trip
    const allMemories = useMemo(() => {
        return days.flatMap(day => day.memories || []);
    }, [days]);

    // Get highlights
    const highlights = useMemo(() => {
        return allMemories.filter(m => m.isHighlight);
    }, [allMemories]);

    // Handlers
    const handleViewDay = (day) => {
        setSelectedDay(day);
    };

    const handleBackToTimeline = () => {
        setSelectedDay(null);
    };

    const handleMediaClick = (memory, memories) => {
        setViewerMemories(memories);
        setViewerIndex(memories.findIndex(m => m.id === memory.id));
        setMediaViewerOpen(true);
    };

    const handleToggleHighlight = async (memoryId) => {
        // Update local state optimistically
        setDays(prevDays => prevDays.map(day => ({
            ...day,
            memories: day.memories?.map(m =>
                m.id === memoryId ? { ...m, isHighlight: !m.isHighlight } : m
            ),
        })));
        
        // Update viewer memories if open
        if (mediaViewerOpen) {
            setViewerMemories(prev => prev.map(m =>
                m.id === memoryId ? { ...m, isHighlight: !m.isHighlight } : m
            ));
        }
        
        // Call API if not using mock data
        if (!useMockData) {
            try {
                await memoriesApi.toggleHighlight(memoryId);
            } catch (err) {
                console.error('Failed to toggle highlight:', err);
                // Revert on error
                setDays(prevDays => prevDays.map(day => ({
                    ...day,
                    memories: day.memories?.map(m =>
                        m.id === memoryId ? { ...m, isHighlight: !m.isHighlight } : m
                    ),
                })));
            }
        }
    };

    const handleEditCaption = async (memoryId, caption) => {
        // Update local state
        setDays(prevDays => prevDays.map(day => ({
            ...day,
            memories: day.memories?.map(m =>
                m.id === memoryId ? { ...m, caption } : m
            ),
        })));
        
        // Update viewer memories if open
        if (mediaViewerOpen) {
            setViewerMemories(prev => prev.map(m =>
                m.id === memoryId ? { ...m, caption } : m
            ));
        }
        
        // Call API if not using mock data
        if (!useMockData) {
            try {
                await memoriesApi.updateCaption(memoryId, caption);
            } catch (err) {
                console.error('Failed to update caption:', err);
            }
        }
    };

    // items: array of { file, caption }. onProgress reports overall 0-100 across all items.
    const handleDeleteMemory = async (memoryId) => {
        // Snapshot for potential revert
        const prevDaysSnapshot = days;

        // Remove from days state
        setDays(prevDays => prevDays.map(day => ({
            ...day,
            memories: day.memories?.filter(m => m.id !== memoryId),
        })));

        // Update the open viewer: drop the deleted item and keep a valid index (close if empty)
        if (mediaViewerOpen) {
            setViewerMemories(prev => {
                const next = prev.filter(m => m.id !== memoryId);
                if (next.length === 0) {
                    setMediaViewerOpen(false);
                } else {
                    setViewerIndex(idx => Math.min(idx, next.length - 1));
                }
                return next;
            });
        }

        // Keep the currently-open day view in sync
        setSelectedDay(prev => prev ? { ...prev, memories: prev.memories?.filter(m => m.id !== memoryId) } : prev);

        if (!useMockData) {
            try {
                await memoriesApi.deleteMemory(memoryId);
            } catch (err) {
                console.error('Failed to delete memory:', err);
                // Revert on error
                setDays(prevDaysSnapshot);
            }
        }
    };

    const handleUpload = async (items, onProgress = null) => {
        if (useMockData) {
            console.log('Mock upload:', items);
            return;
        }

        const total = items.length;
        for (let i = 0; i < total; i++) {
            const { file, caption } = items[i];
            const result = await memoriesApi.uploadMemory(
                tripId,
                uploadDayId,
                file,
                { caption },
                (progress) => {
                    // Blend per-file progress into an overall percentage across all items.
                    if (onProgress) {
                        onProgress(Math.round(((i + progress / 100) / total) * 100));
                    }
                }
            );

            // Add to local state
            const newMemory = {
                id: result.id,
                mediaPath: `${API_BASE_URL.replace('/backend/api', '')}/${result.media_path}`,
                thumbnail: result.thumbnail_path ? `${API_BASE_URL.replace('/backend/api', '')}/${result.thumbnail_path}` : null,
                caption: caption || '',
                isHighlight: false,
                mediaType: file.type.startsWith('video') ? 'video' : 'image',
                takenAt: new Date().toISOString(),
            };

            setDays(prevDays => prevDays.map(day =>
                day.id === uploadDayId
                    ? { ...day, memories: [...(day.memories || []), newMemory] }
                    : day
            ));
        }
        if (onProgress) onProgress(100);
    };

    const handleSaveNote = async (dayId, note) => {
        // Update local state
        setDays(prevDays => prevDays.map(day =>
            day.id === dayId ? { ...day, note } : day
        ));
        if (selectedDay?.id === dayId) {
            setSelectedDay(prev => ({ ...prev, note }));
        }
        
        // Call API if not using mock data
        if (!useMockData) {
            try {
                await memoriesApi.saveDayNote(tripId, dayId, note);
            } catch (err) {
                console.error('Failed to save note:', err);
            }
        }
    };

    const openUploadModal = (dayId) => {
        setUploadDayId(dayId);
        setUploadModalOpen(true);
    };

    // Render loading state
    if (loading) {
        return (
            <div className="tw-p-4 tw-max-w-2xl tw-mx-auto">
                <div className="tw-h-8 tw-w-48 tw-rounded tw-bg-surface-3 tw-animate-pulse tw-mb-4" />
                {[1, 2, 3].map(i => <DayCardSkeleton key={i} />)}
            </div>
        );
    }

    // Render day view
    if (selectedDay) {
        return (
            <div className="tw-p-4 tw-max-w-2xl tw-mx-auto">
                {/* Header */}
                <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
                    <button onClick={handleBackToTimeline} className="tw-p-2 tw-rounded-xl tw-text-muted hover:tw-text-muted-fore hover:tw-bg-surface-3 tw-transition-all tw-flex-shrink-0" aria-label="Back">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"/></svg>
                    </button>
                    <div className="tw-flex-1 tw-min-w-0">
                        <h2 className="tw-text-lg tw-font-bold tw-text-muted-fore tw-truncate">{selectedDay.title}</h2>
                        <p className="tw-text-xs tw-text-muted">
                            {new Date(selectedDay.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                    </div>
                    <button onClick={() => openUploadModal(selectedDay.id)} className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-xl tw-text-sm tw-font-semibold tw-bg-accent/20 tw-text-accent hover:tw-bg-accent/30 tw-transition-all tw-flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="3.2"/><path d="M9 2 7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2z"/></svg>
                        Add
                    </button>
                </div>

                {/* Day note */}
                <NoteEditor
                    dayId={selectedDay.id}
                    initialNote={selectedDay.note}
                    onSave={(note) => handleSaveNote(selectedDay.id, note)}
                />

                {/* Activities with memories */}
                {selectedDay.activities?.length > 0 && (
                    <div className="tw-mb-4">
                        <h3 className="tw-text-sm tw-font-bold tw-text-muted-fore tw-mb-3">Activities</h3>
                        {selectedDay.activities.map(activity => {
                            const activityMemories = selectedDay.memories?.filter(m => m.activityId === activity.id) || [];
                            return (
                                <div key={activity.id} className="tw-rounded-2xl tw-bg-surface-glass tw-backdrop-blur-md tw-border tw-border-surface-border tw-p-4 tw-mb-3">
                                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                                        {activity.time && <span className="tw-text-xs tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-lg tw-bg-accent/15 tw-text-accent">{activity.time}</span>}
                                        <span className="tw-text-sm tw-font-semibold tw-text-muted-fore">{activity.name}</span>
                                    </div>
                                    {activityMemories.length > 0 ? (
                                        <div className="tw-flex tw-gap-2 tw-flex-wrap">
                                            {activityMemories.map(memory => (
                                                <MediaThumbnail key={memory.id} memory={memory} size="medium" onClick={(m) => handleMediaClick(m, selectedDay.memories)} />
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="tw-text-xs tw-text-muted">No photos yet</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Unassigned memories */}
                {selectedDay.memories?.filter(m => !m.activityId).length > 0 && (
                    <div className="tw-mb-4">
                        <h3 className="tw-text-sm tw-font-bold tw-text-muted-fore tw-mb-3">Other Memories</h3>
                        <div className="tw-flex tw-gap-2 tw-flex-wrap">
                            {selectedDay.memories.filter(m => !m.activityId).map(memory => (
                                <MediaThumbnail key={memory.id} memory={memory} size="medium" showCaption onClick={(m) => handleMediaClick(m, selectedDay.memories)} />
                            ))}
                        </div>
                    </div>
                )}

                {/* Empty state */}
                {(!selectedDay.memories || selectedDay.memories.length === 0) && (
                    <div className="tw-rounded-2xl tw-bg-surface-glass tw-backdrop-blur-md tw-border tw-border-surface-border tw-p-10 tw-text-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="tw-text-muted/40 tw-mx-auto tw-mb-3" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                        <h4 className="tw-text-base tw-font-bold tw-text-muted-fore tw-mb-1">No memories yet</h4>
                        <p className="tw-text-sm tw-text-muted tw-mb-4">Start adding photos and videos from this day</p>
                        <button onClick={() => openUploadModal(selectedDay.id)} className="tw-flex tw-items-center tw-gap-2 tw-mx-auto tw-px-4 tw-py-2 tw-rounded-xl tw-text-sm tw-font-semibold tw-bg-accent/20 tw-text-accent hover:tw-bg-accent/30 tw-transition-all">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="3.2"/><path d="M9 2 7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2z"/></svg>
                            Add Memories
                        </button>
                    </div>
                )}

                {/* Upload Modal */}
                <UploadModal
                    open={uploadModalOpen}
                    onClose={() => setUploadModalOpen(false)}
                    dayId={uploadDayId}
                    onUpload={handleUpload}
                />

                {/* Media Viewer */}
                <MediaViewer
                    open={mediaViewerOpen}
                    memories={viewerMemories}
                    currentIndex={viewerIndex}
                    onClose={() => setMediaViewerOpen(false)}
                    onNavigate={setViewerIndex}
                    onToggleHighlight={handleToggleHighlight}
                    onEditCaption={handleEditCaption}
                />
            </div>
        );
    }

    // Render timeline view (default)
    return (
        <div className="tw-p-4 tw-max-w-2xl tw-mx-auto">
            {/* Header */}
            <div className="tw-mb-5">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                    <div>
                        <h1 className="tw-text-xl tw-font-bold tw-text-muted-fore">{trip?.name || 'Trip Memories'}</h1>
                        {trip && (
                            <p className="tw-text-xs tw-text-muted tw-mt-0.5">
                                {new Date(trip.startDate).toLocaleDateString()} – {new Date(trip.endDate).toLocaleDateString()}
                            </p>
                        )}
                    </div>
                    <button onClick={() => navigate(`/trip/${tripId}`)} className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-xl tw-text-xs tw-font-semibold tw-text-accent tw-bg-accent/15 tw-border tw-border-accent/25 hover:tw-bg-accent/25 tw-transition-all">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                        Trip
                    </button>
                </div>

                {/* View mode tabs */}
                <div className="tw-flex tw-gap-1 tw-p-1 tw-rounded-xl tw-bg-surface-3/60 tw-mb-4">
                    {[
                        { key: 'timeline', label: 'Timeline' },
                        { key: 'gallery', label: 'Gallery' },
                        { key: 'highlights', label: 'Highlights' },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setViewMode(tab.key)}
                            className={`tw-flex-1 tw-py-1.5 tw-rounded-lg tw-text-sm tw-font-semibold tw-transition-all ${viewMode === tab.key ? 'tw-bg-surface-2 tw-text-muted-fore tw-shadow-sm' : 'tw-text-muted hover:tw-text-muted-fore'}`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Summary stats */}
                <div className="tw-flex tw-gap-2 tw-flex-wrap">
                    <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-muted tw-px-2.5 tw-py-1 tw-rounded-lg tw-bg-surface-3/60 tw-border tw-border-surface-border/40">
                        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        {days.length} days
                    </span>
                    <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-muted tw-px-2.5 tw-py-1 tw-rounded-lg tw-bg-surface-3/60 tw-border tw-border-surface-border/40">
                        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="12" r="3.2"/><path d="M9 2 7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2z"/></svg>
                        {allMemories.length} memories
                    </span>
                    {highlights.length > 0 && (
                        <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-amber-400 tw-px-2.5 tw-py-1 tw-rounded-lg tw-bg-amber-500/10 tw-border tw-border-amber-500/20">
                            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                            {highlights.length} highlights
                        </span>
                    )}
                </div>
            </div>

            {/* Content based on view mode */}
            {viewMode === 'timeline' && (
                <div>
                    {days.length > 0 ? (
                        days.map(day => (
                            <DayCard
                                key={day.id}
                                day={day}
                                memories={day.memories || []}
                                note={day.note}
                                onViewDay={handleViewDay}
                                onMediaClick={handleMediaClick}
                            />
                        ))
                    ) : (
                        <div className="tw-rounded-2xl tw-bg-surface-glass tw-backdrop-blur-md tw-border tw-border-surface-border tw-p-10 tw-text-center">
                            <p className="tw-text-base tw-font-bold tw-text-muted-fore">No days in this trip yet</p>
                        </div>
                    )}
                </div>
            )}

            {viewMode === 'gallery' && (
                <div>
                    {allMemories.length > 0 ? (
                        <div className="tw-grid tw-grid-cols-3 tw-gap-1.5">
                            {allMemories.map(memory => {
                                const hasNote = !!(memory.caption && memory.caption.trim());
                                return (
                                <div key={memory.id} className="tw-relative tw-cursor-pointer tw-rounded-xl tw-overflow-hidden tw-aspect-square tw-group" onClick={() => handleMediaClick(memory, allMemories)} title={hasNote ? memory.caption : undefined}>
                                    <img src={memory.thumbnail || memory.mediaPath} alt={memory.caption || 'Memory'} loading="lazy" className="tw-w-full tw-h-full tw-object-cover" />
                                    {memory.isHighlight && (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="tw-absolute tw-top-1.5 tw-right-1.5 tw-text-amber-400 tw-drop-shadow-md" aria-hidden="true"><path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                                    )}
                                    {hasNote && <NoteBadge />}
                                    {hasNote && (
                                        <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-bg-black/70 tw-py-1 tw-px-1.5 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-pointer-events-none">
                                            <p className="tw-text-xs tw-text-white tw-line-clamp-2">{memory.caption}</p>
                                        </div>
                                    )}
                                </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="tw-rounded-2xl tw-bg-surface-glass tw-backdrop-blur-md tw-border tw-border-surface-border tw-p-10 tw-text-center">
                            <p className="tw-text-base tw-font-bold tw-text-muted-fore">No memories yet</p>
                        </div>
                    )}
                </div>
            )}

            {viewMode === 'highlights' && (
                <div>
                    {highlights.length > 0 ? (
                        <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                            {highlights.map(memory => (
                                <div key={memory.id} className="tw-relative tw-cursor-pointer tw-rounded-xl tw-overflow-hidden tw-aspect-square" onClick={() => handleMediaClick(memory, highlights)}>
                                    <img src={memory.thumbnail || memory.mediaPath} alt={memory.caption || 'Memory'} loading="lazy" className="tw-w-full tw-h-full tw-object-cover" />
                                    {(memory.caption || memory.takenAt) && (
                                        <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-bg-gradient-to-t tw-from-black/70 tw-to-transparent tw-px-2.5 tw-py-2">
                                            {memory.caption && <p className="tw-text-xs tw-text-white tw-font-medium tw-truncate">{memory.caption}</p>}
                                            {memory.takenAt && <p className="tw-text-xs tw-text-white/60">{new Date(memory.takenAt).toLocaleDateString()}</p>}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="tw-rounded-2xl tw-bg-surface-glass tw-backdrop-blur-md tw-border tw-border-surface-border tw-p-10 tw-text-center">
                            <p className="tw-text-base tw-font-bold tw-text-muted-fore tw-mb-1">No highlights yet</p>
                            <p className="tw-text-sm tw-text-muted">Mark your favorite memories as highlights!</p>
                        </div>
                    )}
                </div>
            )}

            {/* FAB for quick add */}
            <button
                onClick={() => openUploadModal(days[0]?.id)}
                className="tw-fixed tw-bottom-6 tw-right-6 tw-w-12 tw-h-12 tw-rounded-full tw-bg-accent tw-text-white tw-flex tw-items-center tw-justify-center tw-shadow-xl hover:tw-opacity-90 tw-transition-all tw-z-40"
                aria-label="Add memory"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="12" r="3.2"/><path d="M9 2 7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2z"/></svg>
            </button>

            {/* Upload Modal */}
            <UploadModal
                open={uploadModalOpen}
                onClose={() => setUploadModalOpen(false)}
                dayId={uploadDayId}
                onUpload={handleUpload}
            />

            {/* Media Viewer */}
            <MediaViewer
                open={mediaViewerOpen}
                memories={viewerMemories}
                currentIndex={viewerIndex}
                onClose={() => setMediaViewerOpen(false)}
                onNavigate={setViewerIndex}
                onToggleHighlight={handleToggleHighlight}
                onEditCaption={handleEditCaption}
                onDelete={handleDeleteMemory}
            />
        </div>
    );
};

export default MemoriesPage;
